import { useCallback } from 'react';
import { FriendLocation, TemporaryLocationWithFriend } from '../../../types';

/**
 * Hook to manage map navigation and highlighting
 * Encapsulates all navigation logic for markers, temp locations, and official addresses
 */
export const useMapNavigation = (
  mapInstance: L.Map | null,
  locations: FriendLocation[],
  setHighlightedFriendId: (id: number | null) => void
) => {
  /**
   * Navigate to a friend's official address marker
   */
  const handleOfficialAddressClick = useCallback((friendId: number) => {
    const friend = locations.find(loc => loc.id === friendId);
    if (friend && friend.latitude && friend.longitude && mapInstance) {
      // Navigate to friend's official address marker with animation
      mapInstance.flyTo([friend.latitude, friend.longitude], 15, {
        animate: true,
        duration: 1.5,
        easeLinearity: 0.25
      });

      // Open the popup for the friend marker after animation
      setTimeout(() => {
        setHighlightedFriendId(friendId);
        setTimeout(() => {
          setHighlightedFriendId(null);
        }, 2000);
      }, 1600); // Wait for flyTo animation to complete
    }
  }, [mapInstance, locations, setHighlightedFriendId]);

  /**
   * Navigate to a friend's temporary location marker
   */
  const handleTempLocationClick = useCallback((tempLocation: TemporaryLocationWithFriend) => {
    if (mapInstance && tempLocation.latitude && tempLocation.longitude) {
      mapInstance.setView([tempLocation.latitude, tempLocation.longitude], 15);

      // Open the popup of the temp location marker after navigation
      setTimeout(() => {
        // The temp location marker has ID = tempLocation.id + 1000000
        setHighlightedFriendId(tempLocation.id + 1000000);
        setTimeout(() => {
          setHighlightedFriendId(null);
        }, 2000);
      }, 500);
    }
  }, [mapInstance, setHighlightedFriendId]);

  return {
    handleOfficialAddressClick,
    handleTempLocationClick,
  };
};
